import { z } from 'zod';
import { optionalText } from './field.js';
import { MAX_NOTE_TEXT, MAX_NOTE_TITLE } from './note.js';
import { sameNoteText } from './note-mdast.js';
/**
 * The unsaved state of a note form, saved automatically (L2; called the
 * "draft" until N1, when `draft` became a status of the note itself).
 *
 * **An autosave is not the note.** The editor cannot write into the row: what
 * stands there is the last saved version, so writing into it would make
 * "Abbrechen" impossible, and a crash halfway through a rephrasing would turn
 * half a sentence into the valid documentation. So the form under the cursor
 * lives here until "Speichern" or "Als Entwurf speichern" makes a note of it.
 *
 * **Every autosave belongs to a note** (N1b). A note exists from the moment it
 * is started, so there is no form without a row behind it, and the key is
 * `(user, note)` — nothing else.
 *
 * That also settles what this is not: no versioning, no history, no merge.
 * There is always exactly one autosave, and the newer overwrites the older.
 *
 * **The autosave mirrors the form, gaps included.** Every field the form saves
 * is here — title, text, date, type, activity — because anything that is only
 * taken over on "Speichern" can otherwise be lost. Each may be empty: a save
 * running while the date field is briefly blank during retyping must not
 * fail, and an emptied title is a state of the form like any other. Whether an
 * autosave is worth offering is decided by comparing it with the note
 * (`sameNoteContent`), never by emptiness.
 */
export const noteAutosaveInputSchema = z.object({
    activityId: z.uuid().nullable().default(null),
    noteTypeId: z.uuid().nullable().default(null),
    noteDate: z.iso.date().nullable().default(null),
    /** Trimmed and folded to null like the note's own fields, with the same
     *  upper bounds: an autosave may not hold more than the note it will
     *  become. */
    title: optionalText(MAX_NOTE_TITLE),
    text: optionalText(MAX_NOTE_TEXT),
});
export const noteAutosaveSchema = z.object({
    id: z.uuid(),
    noteId: z.uuid(),
    activityId: z.uuid().nullable(),
    noteTypeId: z.uuid().nullable(),
    noteDate: z.iso.date().nullable(),
    title: z.string().nullable(),
    text: z.string().nullable(),
    /** When it was last written — what the question on opening reports. */
    updatedAt: z.iso.datetime(),
});
/** Trimmed, blank folded to null — the edge the schemas draw as well. */
function folded(value) {
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
}
/**
 * Whether two states of a note form are the same (N1b) — the one comparison
 * behind three decisions: whether an autosave is offered (server), whether
 * one is written at all, and whether "Abbrechen" has anything to ask about
 * (client).
 *
 * The text is compared as a tree (`sameNoteText`), so a text the editor
 * merely rewrote on loading is not a change. The other fields are compared
 * directly, the title after trimming.
 */
export function sameNoteContent(a, b) {
    return (a.activityId === b.activityId &&
        a.noteTypeId === b.noteTypeId &&
        a.noteDate === b.noteDate &&
        folded(a.title) === folded(b.title) &&
        sameNoteText(a.text, b.text));
}
/**
 * How the editor saves: three seconds after the last keystroke, and at the
 * latest every twenty even if nobody pauses.
 *
 * Three seconds because pauses of that length happen constantly between
 * sentences, so most saves land while the writer is thinking. Twenty is the
 * actual promise — never more than twenty seconds of work at stake. There is
 * deliberately no `beforeunload` handler beside them: the case this exists
 * for is a browser that crashes, and a crash fires no event.
 */
export const NOTE_AUTOSAVE_IDLE_MS = 3_000;
export const NOTE_AUTOSAVE_MAX_WAIT_MS = 20_000;
/** How long an untouched autosave survives, swept at login like an expired
 *  session. Long enough for "I meant to come back to it", short enough that
 *  unsaved documentation does not pile up unnoticed. */
export const NOTE_AUTOSAVE_MAX_AGE_DAYS = 30;
//# sourceMappingURL=note-autosave.js.map